import { useCallback, useEffect, useState } from 'react'
import { apiService } from '@/services/api'

export interface UsageQuota {
    monthlyUsagePercent: number
    resetAt: string
    isUnlimited: boolean
}

export interface UseUsageQuotaReturn {
    usage: UsageQuota | null
    loading: boolean
    error: string
    refetch: () => void
}

export interface UseUsageQuotaOptions {
    /** When false, the hook skips the network request (useful for unauthenticated callers). Defaults to true. */
    enabled?: boolean
}

export function useUsageQuota(options: UseUsageQuotaOptions = {}): UseUsageQuotaReturn {
    const { enabled = true } = options
    const [usage, setUsage] = useState<UsageQuota | null>(null)
    const [loading, setLoading] = useState(enabled)
    const [error, setError] = useState('')

    const fetchUsage = useCallback(async () => {
        setLoading(true)
        setError('')
        try {
            const data = await apiService.getCurrentUsage()
            setUsage({
                monthlyUsagePercent: data.monthly_usage_percent,
                resetAt: data.reset_at,
                isUnlimited: data.is_unlimited,
            })
        } catch {
            setError('errors:llmQuota.loadFailed')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        if (!enabled) {
            setLoading(false)
            setUsage(null)
            return
        }
        void fetchUsage()
    }, [fetchUsage, enabled])

    const refetch = useCallback(() => {
        if (!enabled) return
        void fetchUsage()
    }, [fetchUsage, enabled])

    return { usage, loading, error, refetch }
}
